import {
	NumberInput,
	SegmentedControl,
	SimpleGrid,
	Stack,
	Text,
} from "@mantine/core";
import type { AssistantSettingsDto } from "@/types/ai-assistant-admin";
import type { WallNocMicMode } from "@/types/ai-assistant-voice";
import { BELOW_INPUT_ORDER } from "./limit-groups";
import { VOICE_LIMIT_RULES } from "./voice-settings.logic";

export type WallNocKey = "wallNocMicMode" | "wallNocVolume" | "wallNocSettleMs";

interface Props {
	form: Pick<AssistantSettingsDto, WallNocKey>;
	errors: Partial<Record<string, string>>;
	disabled: boolean;
	onChange: <K extends WallNocKey>(
		key: K,
		value: AssistantSettingsDto[K],
	) => void;
}

const MIC_OPTIONS: { value: WallNocMicMode; label: string }[] = [
	{ value: "ptt", label: "Tekan-untuk-bicara" },
	{ value: "gate", label: "Bisu otomatis" },
	{ value: "ptt_gate", label: "Keduanya" },
];

export const MIC_HELP: Record<WallNocMicMode, string> = {
	ptt: "Disarankan. Mikrofon tertutup sampai tombol ditahan, jadi obrolan di ruangan tidak sampai ke Jenna.",
	gate: "Perlu uji lapangan. Mikrofon tetap terbuka tetapi dibisukan otomatis saat Jenna bicara. Di ruangan bising sesi bisa terus terbuka sampai batas satu sesi dan menghabiskan kuota kiosk.",
	ptt_gate:
		"Perlu uji lapangan. Mikrofon tertutup sampai tombol ditahan, dan dibisukan otomatis saat Jenna bicara.",
};

/** Sub-setelan Mode NOC: perilaku mikrofon, volume suara Jenna, ekor hening mic otomatis. */
export function WallNocFields({ form, errors, disabled, onChange }: Props) {
	const numberInput = (key: "wallNocVolume" | "wallNocSettleMs") => {
		const rule = VOICE_LIMIT_RULES[key];
		return (
			<NumberInput
				label={rule.label}
				description={rule.hint}
				inputWrapperOrder={[...BELOW_INPUT_ORDER]}
				min={rule.min}
				max={rule.max}
				allowDecimal={false}
				value={form[key]}
				onChange={(v) => onChange(key, typeof v === "number" ? v : Number.NaN)}
				error={errors[key]}
				disabled={disabled}
			/>
		);
	};
	return (
		<Stack gap="xs" data-wall-noc-fields>
			<Text size="sm" fw={500}>
				Mikrofon Mode NOC
			</Text>
			<SegmentedControl
				data={MIC_OPTIONS}
				value={form.wallNocMicMode}
				onChange={(v) => onChange("wallNocMicMode", v as WallNocMicMode)}
				disabled={disabled}
			/>
			<Text size="xs" c={errors.wallNocMicMode ? "red" : "dimmed"}>
				{errors.wallNocMicMode ?? MIC_HELP[form.wallNocMicMode]}
			</Text>
			<SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="md">
				{numberInput("wallNocVolume")}
				{numberInput("wallNocSettleMs")}
			</SimpleGrid>
		</Stack>
	);
}
